import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { useAuth } from '../contexts/AuthContext';
import NotificationService from '../services/notificationService';
import type {
  SupportAuthorSide,
  SupportTicket,
  SupportTicketCategory,
  SupportTicketMessage,
  SupportTicketStatus,
} from '../types/supportTicket';
import {
  computeMediaPurgeAt,
  hasUnreadClientReply,
  hasUnreadSupportReply,
  nextStatusAfterMessage,
  previewFromBody,
} from '../utils/supportTicket';

function toDate(value: unknown): Date {
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  if (value instanceof Date) return value;
  return new Date();
}

function mapTicket(id: string, data: Record<string, unknown>): SupportTicket {
  return {
    id,
    businessId: String(data.businessId || ''),
    businessName: String(data.businessName || ''),
    createdByUserId: String(data.createdByUserId || ''),
    createdByName: String(data.createdByName || ''),
    openedByUserId: data.openedByUserId ? String(data.openedByUserId) : undefined,
    openedByName: data.openedByName ? String(data.openedByName) : undefined,
    subject: String(data.subject || ''),
    category: data.category as SupportTicketCategory | undefined,
    status: (data.status as SupportTicketStatus) || 'open',
    lastMessageAt: toDate(data.lastMessageAt),
    lastMessagePreview: String(data.lastMessagePreview || ''),
    lastMessageAuthorSide: data.lastMessageAuthorSide as SupportAuthorSide | undefined,
    clientLastReadAt: data.clientLastReadAt ? toDate(data.clientLastReadAt) : undefined,
    staffLastReadAt: data.staffLastReadAt ? toDate(data.staffLastReadAt) : undefined,
    firstNauvatekReplyAt: data.firstNauvatekReplyAt ? toDate(data.firstNauvatekReplyAt) : undefined,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    resolvedAt: data.resolvedAt ? toDate(data.resolvedAt) : undefined,
    resolvedByUserId: data.resolvedByUserId ? String(data.resolvedByUserId) : undefined,
    mediaPurgeAt: data.mediaPurgeAt ? toDate(data.mediaPurgeAt) : undefined,
    mediaPurgePending: data.mediaPurgePending === true,
    mediaPurgedAt: data.mediaPurgedAt ? toDate(data.mediaPurgedAt) : undefined,
  };
}

function mapMessage(id: string, data: Record<string, unknown>): SupportTicketMessage {
  const attachmentsRaw = Array.isArray(data.attachments) ? data.attachments : [];
  return {
    id,
    authorUserId: String(data.authorUserId || ''),
    authorName: String(data.authorName || ''),
    authorSide: (data.authorSide as SupportAuthorSide) || 'client',
    body: String(data.body || ''),
    attachments: attachmentsRaw as SupportTicketMessage['attachments'],
    createdAt: toDate(data.createdAt),
    mediaPurged: data.mediaPurged === true,
  };
}

function chunkIds<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/** Tickets du user connecté pour le business courant (page Aide) — live. */
export function useMySupportTickets() {
  const { currentBusiness, user } = useAuth();
  const businessId = currentBusiness?.id;
  const userId = user?.uid;
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!businessId || !userId) {
      setTickets([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const q = query(
      collection(db, COLLECTIONS.supportTickets),
      where('createdByUserId', '==', userId),
      where('businessId', '==', businessId),
      orderBy('lastMessageAt', 'desc'),
      limit(100)
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setTickets(snap.docs.map((d) => mapTicket(d.id, d.data() as Record<string, unknown>)));
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
    return () => unsubscribe();
  }, [businessId, userId]);

  return { data: tickets, isLoading };
}

/** Nombre de tickets avec réponse support non lue (badge Aide). */
export function useMySupportUnreadCount() {
  const { data: tickets = [] } = useMySupportTickets();
  const count = tickets.filter((t) => hasUnreadSupportReply(t)).length;
  return { data: count };
}

/** Marque la conversation comme lue côté client. */
export function useMarkSupportTicketRead() {
  return useMutation({
    mutationFn: async (ticketId: string) => {
      await updateDoc(doc(db, COLLECTIONS.supportTickets, ticketId), {
        clientLastReadAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },
  });
}

/** Marque la conversation comme lue côté NauvaTek (SA/CSM). */
export function useMarkSupportTicketReadByStaff() {
  return useMutation({
    mutationFn: async (ticketId: string) => {
      await updateDoc(doc(db, COLLECTIONS.supportTickets, ticketId), {
        staffLastReadAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },
  });
}

/** Inbox NauvaTek : SA = tous · CSM = businesses liés — live. */
export function useNauvatekSupportTickets(
  statusFilter?: SupportTicketStatus | 'all',
  options?: { enabled?: boolean }
) {
  const { isSuperAdmin, isCSM, userProfile } = useAuth();
  const canAccess = isSuperAdmin || isCSM;
  const enabled = (options?.enabled ?? true) && canAccess;
  const businessIds = userProfile?.businessIds ?? [];
  const businessIdsKey = businessIds.join(',');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!enabled || (!isSuperAdmin && businessIds.length === 0)) {
      setTickets([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const applyFilter = (list: SupportTicket[]) => {
      const filtered =
        statusFilter && statusFilter !== 'all'
          ? list.filter((t) => t.status === statusFilter)
          : list;
      return filtered.sort((a, b) => b.lastMessageAt.getTime() - a.lastMessageAt.getTime());
    };

    if (isSuperAdmin) {
      const q = query(
        collection(db, COLLECTIONS.supportTickets),
        orderBy('lastMessageAt', 'desc'),
        limit(150)
      );
      const unsubscribe = onSnapshot(
        q,
        (snap) => {
          const list = snap.docs.map((d) => mapTicket(d.id, d.data() as Record<string, unknown>));
          setTickets(applyFilter(list));
          setIsLoading(false);
        },
        () => setIsLoading(false)
      );
      return () => unsubscribe();
    }

    const chunks = chunkIds(businessIds, 10);
    const chunkLists: SupportTicket[][] = chunks.map(() => []);
    const unsubscribes = chunks.map((ids, index) =>
      onSnapshot(
        query(
          collection(db, COLLECTIONS.supportTickets),
          where('businessId', 'in', ids),
          orderBy('lastMessageAt', 'desc'),
          limit(100)
        ),
        (snap) => {
          chunkLists[index] = snap.docs.map((d) =>
            mapTicket(d.id, d.data() as Record<string, unknown>)
          );
          const merged = chunkLists.flat();
          const byId = new Map(merged.map((t) => [t.id, t]));
          setTickets(applyFilter(Array.from(byId.values())));
          setIsLoading(false);
        },
        () => setIsLoading(false)
      )
    );

    return () => unsubscribes.forEach((u) => u());
  }, [enabled, isSuperAdmin, businessIdsKey, statusFilter, businessIds]);

  return { data: tickets, isLoading };
}

/** Nombre de tickets avec message client non lu (badge Support). */
export function useNauvatekSupportUnreadCount(options?: { enabled?: boolean }) {
  const { isSuperAdmin, isCSM } = useAuth();
  const canAccess = isSuperAdmin || isCSM;
  const enabled = (options?.enabled ?? true) && canAccess;
  const { data: tickets = [] } = useNauvatekSupportTickets('all', { enabled });
  const count = enabled ? tickets.filter((t) => hasUnreadClientReply(t)).length : 0;
  return { data: count };
}

export function useSupportTicket(ticketId: string | undefined) {
  const { user, isSuperAdmin, isCSM, userProfile } = useAuth();
  const userId = user?.uid;
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!ticketId || !userId) {
      setTicket(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, COLLECTIONS.supportTickets, ticketId),
      (snap) => {
        if (!snap.exists()) {
          setTicket(null);
          setError(new Error('Ticket introuvable'));
          setIsLoading(false);
          return;
        }
        const mapped = mapTicket(snap.id, snap.data() as Record<string, unknown>);
        const isOwner = mapped.createdByUserId === userId;
        const isStaff =
          isSuperAdmin || (isCSM && (userProfile?.businessIds ?? []).includes(mapped.businessId));
        if (!isOwner && !isStaff) {
          setTicket(null);
          setError(new Error('Accès refusé à ce ticket.'));
          setIsLoading(false);
          return;
        }
        setTicket(mapped);
        setError(null);
        setIsLoading(false);
      },
      (err) => {
        setError(err as Error);
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [ticketId, userId, isSuperAdmin, isCSM, userProfile?.businessIds]);

  return { data: ticket, isLoading, error };
}

export function useSupportTicketMessages(ticketId: string | undefined) {
  const [messages, setMessages] = useState<SupportTicketMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!ticketId) {
      setMessages([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const q = query(
      collection(db, COLLECTIONS.supportTickets, ticketId, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(300)
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setMessages(snap.docs.map((d) => mapMessage(d.id, d.data() as Record<string, unknown>)));
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [ticketId]);

  return { data: messages, isLoading };
}

type CreateTicketInput = {
  subject: string;
  body: string;
  category?: SupportTicketCategory;
  files?: File[];
};

export function useCreateSupportTicket() {
  const { currentBusiness, user, userProfile } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTicketInput) => {
      if (!currentBusiness?.id || !user?.uid) {
        throw new Error('Business ou utilisateur manquant.');
      }
      const subject = input.subject.trim();
      const body = input.body.trim();
      const files = input.files || [];
      if (!subject) throw new Error('Indiquez un sujet.');
      if (!body && files.length === 0) {
        throw new Error('Ajoutez un message ou une pièce jointe.');
      }

      const { uploadSupportTicketAttachment } = await import('./useSupportTicketAttachments');

      const authorName = userProfile?.name || user.displayName || user.email || 'Utilisateur';
      const now = serverTimestamp();

      const ticketRef = await addDoc(collection(db, COLLECTIONS.supportTickets), {
        businessId: currentBusiness.id,
        businessName: currentBusiness.name || '',
        createdByUserId: user.uid,
        createdByName: authorName,
        subject,
        category: input.category || 'other',
        status: 'open',
        lastMessageAt: now,
        lastMessagePreview: previewFromBody(body, files.length > 0),
        lastMessageAuthorSide: 'client',
        clientLastReadAt: now,
        createdAt: now,
        updatedAt: now,
      });

      const attachments = [];
      for (const file of files) {
        attachments.push(
          await uploadSupportTicketAttachment(currentBusiness.id, ticketRef.id, file)
        );
      }

      await addDoc(collection(db, COLLECTIONS.supportTickets, ticketRef.id, 'messages'), {
        authorUserId: user.uid,
        authorName,
        authorSide: 'client',
        body,
        attachments,
        createdAt: now,
      });

      try {
        await NotificationService.createSupportTicketCreatedNotification({
          businessId: currentBusiness.id,
          supportTicketId: ticketRef.id,
          subject,
          authorName,
          triggeredBy: user.uid,
        });
      } catch (e) {
        console.warn('Notification support créée non envoyée', e);
      }

      return ticketRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
    },
  });
}

type CreateStaffTicketInput = {
  businessId: string;
  businessName: string;
  employeeUserId: string;
  employeeName: string;
  subject: string;
  body: string;
  category?: SupportTicketCategory;
  files?: File[];
};

/** SA / CSM ouvre un ticket pour un employé du business. */
export function useCreateSupportTicketByStaff() {
  const { user, userProfile, isSuperAdmin, isCSM } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateStaffTicketInput) => {
      if (!user?.uid || !(isSuperAdmin || isCSM)) {
        throw new Error('Réservé à l’équipe NauvaTek.');
      }
      if (isCSM && !(userProfile?.businessIds ?? []).includes(input.businessId)) {
        throw new Error('Business non autorisé pour ce CSM.');
      }
      const subject = input.subject.trim();
      const body = input.body.trim();
      const files = input.files || [];
      if (!input.businessId) throw new Error('Sélectionnez un business.');
      if (!input.employeeUserId) throw new Error('Sélectionnez un employé.');
      if (!subject) throw new Error('Indiquez un sujet.');
      if (!body && files.length === 0) {
        throw new Error('Ajoutez un message ou une pièce jointe.');
      }

      const { uploadSupportTicketAttachment } = await import('./useSupportTicketAttachments');
      const staffName = userProfile?.name || user.displayName || user.email || 'NauvaTek';
      const now = serverTimestamp();

      const ticketRef = await addDoc(collection(db, COLLECTIONS.supportTickets), {
        businessId: input.businessId,
        businessName: input.businessName || '',
        createdByUserId: input.employeeUserId,
        createdByName: input.employeeName,
        openedByUserId: user.uid,
        openedByName: staffName,
        subject,
        category: input.category || 'other',
        status: 'waiting_client',
        lastMessageAt: now,
        lastMessagePreview: previewFromBody(body, files.length > 0),
        lastMessageAuthorSide: 'nauvatek',
        staffLastReadAt: now,
        firstNauvatekReplyAt: now,
        createdAt: now,
        updatedAt: now,
      });

      const attachments = [];
      for (const file of files) {
        attachments.push(
          await uploadSupportTicketAttachment(input.businessId, ticketRef.id, file)
        );
      }

      await addDoc(collection(db, COLLECTIONS.supportTickets, ticketRef.id, 'messages'), {
        authorUserId: user.uid,
        authorName: staffName,
        authorSide: 'nauvatek',
        body,
        attachments,
        createdAt: now,
      });

      try {
        await NotificationService.createSupportTicketOpenedForYouNotification({
          businessId: input.businessId,
          supportTicketId: ticketRef.id,
          subject,
          recipientUserId: input.employeeUserId,
          triggeredBy: user.uid,
          triggeredByUser: staffName,
        });
      } catch (e) {
        console.warn('Notification ticket staff→employé non envoyée', e);
      }

      return ticketRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
    },
  });
}

type ReplyInput = {
  ticketId: string;
  businessId: string;
  body: string;
  files?: File[];
  authorSide: SupportAuthorSide;
};

export function useReplySupportTicket() {
  const { user, userProfile } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ReplyInput) => {
      if (!user?.uid) throw new Error('Utilisateur non connecté.');
      const body = input.body.trim();
      const files = input.files || [];
      if (!body && files.length === 0) {
        throw new Error('Ajoutez un message ou une pièce jointe.');
      }

      const { uploadSupportTicketAttachment } = await import('./useSupportTicketAttachments');

      const ticketRef = doc(db, COLLECTIONS.supportTickets, input.ticketId);
      const ticketSnap = await getDoc(ticketRef);
      if (!ticketSnap.exists()) throw new Error('Ticket introuvable.');
      const ticket = mapTicket(ticketSnap.id, ticketSnap.data() as Record<string, unknown>);
      if (ticket.status === 'resolved') {
        throw new Error('Ce ticket est clos. Ouvrez-en un nouveau si besoin.');
      }

      const attachments = [];
      for (const file of files) {
        attachments.push(
          await uploadSupportTicketAttachment(input.businessId, input.ticketId, file)
        );
      }

      const authorName = userProfile?.name || user.displayName || user.email || 'Utilisateur';
      const nextStatus = nextStatusAfterMessage(ticket.status, input.authorSide);
      const preview = previewFromBody(body, attachments.length > 0);
      const now = serverTimestamp();

      const batch = writeBatch(db);
      const messageRef = doc(collection(db, COLLECTIONS.supportTickets, input.ticketId, 'messages'));
      batch.set(messageRef, {
        authorUserId: user.uid,
        authorName,
        authorSide: input.authorSide,
        body,
        attachments,
        createdAt: now,
      });
      batch.update(ticketRef, {
        status: nextStatus,
        lastMessageAt: now,
        lastMessagePreview: preview,
        lastMessageAuthorSide: input.authorSide,
        ...(input.authorSide === 'client' ? { clientLastReadAt: now } : {}),
        ...(input.authorSide === 'nauvatek' ? { staffLastReadAt: now } : {}),
        ...(input.authorSide === 'nauvatek' && !ticket.firstNauvatekReplyAt
          ? { firstNauvatekReplyAt: now }
          : {}),
        updatedAt: now,
      });
      await batch.commit();

      if (input.authorSide === 'nauvatek' && ticket.createdByUserId !== user.uid) {
        try {
          await NotificationService.createSupportTicketReplyNotification({
            businessId: ticket.businessId,
            supportTicketId: ticket.id,
            subject: ticket.subject,
            recipientUserId: ticket.createdByUserId,
            triggeredBy: user.uid,
            triggeredByUser: authorName,
          });
        } catch (e) {
          console.warn('Notification réponse support non envoyée', e);
        }
      }
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
      queryClient.invalidateQueries({ queryKey: ['supportTickets', 'messages', vars.ticketId] });
    },
  });
}

export function useResolveSupportTicket() {
  const { user, userProfile, isSuperAdmin, isCSM } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ticketId: string) => {
      if (!user?.uid || !(isSuperAdmin || isCSM)) {
        throw new Error('Seule l’équipe NauvaTek peut clôturer un ticket.');
      }
      const ticketRef = doc(db, COLLECTIONS.supportTickets, ticketId);
      const snap = await getDoc(ticketRef);
      if (!snap.exists()) throw new Error('Ticket introuvable.');
      const ticket = mapTicket(snap.id, snap.data() as Record<string, unknown>);
      if (ticket.status === 'resolved') return;

      const resolvedAt = new Date();
      await updateDoc(ticketRef, {
        status: 'resolved',
        resolvedAt,
        resolvedByUserId: user.uid,
        mediaPurgeAt: computeMediaPurgeAt(resolvedAt),
        mediaPurgePending: true,
        updatedAt: serverTimestamp(),
      });

      try {
        await NotificationService.createSupportTicketResolvedNotification({
          businessId: ticket.businessId,
          supportTicketId: ticket.id,
          subject: ticket.subject,
          recipientUserId: ticket.createdByUserId,
          triggeredBy: user.uid,
          triggeredByUser: userProfile?.name || user.email || 'NauvaTek',
        });
      } catch (e) {
        console.warn('Notification résolution support non envoyée', e);
      }
    },
    onSuccess: (_data, ticketId) => {
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
      queryClient.invalidateQueries({ queryKey: ['supportTickets', 'one', ticketId] });
    },
  });
}

/** Compteur badges inbox (tickets non résolus visibles staff). */
export function useNauvatekOpenSupportTicketsCount(options?: { enabled?: boolean }) {
  const { isSuperAdmin, isCSM, userProfile } = useAuth();
  const canAccess = isSuperAdmin || isCSM;
  const enabled = (options?.enabled ?? true) && canAccess;

  return useQuery({
    queryKey: ['supportTickets', 'nauvatek-open-count', isSuperAdmin, userProfile?.businessIds],
    enabled,
    queryFn: async () => {
      if (isSuperAdmin) {
        const openSnap = await getDocs(
          query(
            collection(db, COLLECTIONS.supportTickets),
            where('status', '==', 'open'),
            limit(100)
          )
        );
        const waitingSnap = await getDocs(
          query(
            collection(db, COLLECTIONS.supportTickets),
            where('status', '==', 'waiting_client'),
            limit(100)
          )
        );
        return openSnap.size + waitingSnap.size;
      }
      const businessIds = userProfile?.businessIds ?? [];
      let count = 0;
      for (const ids of chunkIds(businessIds, 10)) {
        const snap = await getDocs(
          query(
            collection(db, COLLECTIONS.supportTickets),
            where('businessId', 'in', ids),
            orderBy('lastMessageAt', 'desc'),
            limit(100)
          )
        );
        snap.docs.forEach((d) => {
          const status = d.data().status;
          if (status === 'open' || status === 'waiting_client') count += 1;
        });
      }
      return count;
    },
    refetchInterval: enabled ? 60_000 : false,
  });
}
